import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { parseClassTimeRange } from '../../Model/Share/Normal/useClassTime.js';

// 높이 계산 상수
const CELL_HEIGHT = 78;
const GAP = 8;
const FIRST_INDEX_MARGIN = 4;
const OTHER_INDEX_MARGIN = 8;
const TEXT_OFFSET = 21;

// 시간 범위에서 높이 계산
const calcHeightFromRate = (rate, conversionRate) => {
  if (rate <= 1 || rate < 1.5) return CELL_HEIGHT;

  return (((rate - 1) * conversionRate) + 1) * CELL_HEIGHT;
};

function NewTimeDivisionLine (props) {
  const { conversionRate, totalData, index, rowCount = 1 } = props;

  const [isNeedNewTime, setIsNeedNewTime] = useState(false);
  const [viewTime, setViewTime] = useState('');
  const [lengthRate, setLengthRate] = useState(1);

  useEffect(() => {
    if (!totalData || totalData.length === 0) return;

    const data = totalData[index];

    if (index === 0) setIsNeedNewTime(true);
    else if (totalData[index][0].viewTime !== totalData[index - 1][0].viewTime) setIsNeedNewTime(true);

    let finalStartTime = 0;
    let finalEndTime = 0;
    let minimumIndex = 0;

    for (let i = 0; i < data.length; i++) {
      if (data[i].className !== '수업없음') {
        const { startValue, endValue } = parseClassTimeRange(data[i].classTime);

        if (finalStartTime === 0) {
          finalStartTime = startValue;
          minimumIndex = i;
        }
        else if (finalStartTime > startValue) {
          finalStartTime = startValue;
          minimumIndex = i;
        }

        if (finalEndTime === 0) finalEndTime = endValue;
        else if (finalEndTime < endValue) finalEndTime = endValue;
      }
    }

    const finalRate = finalEndTime - finalStartTime;

    if (finalRate <= 1 || finalRate < 1.5) setLengthRate(1);
    else setLengthRate((((finalRate - 1) * conversionRate) + 1));

    setViewTime(data[minimumIndex].viewTime);
  }, [totalData, index, conversionRate])

  // 행의 실제 시간 범위를 계산하여 높이를 반환
  const calcRowHeight = useCallback((rowData) => {
    let rowStartTime = 0;
    let rowEndTime = 0;

    for (let j = 0; j < rowData.length; j++) {
      if (rowData[j].className !== '수업없음') {
        const { startValue, endValue } = parseClassTimeRange(rowData[j].classTime);

        if (rowStartTime === 0) rowStartTime = startValue;
        else if (rowStartTime > startValue) rowStartTime = startValue;

        if (rowEndTime === 0) rowEndTime = endValue;
        else if (rowEndTime < endValue) rowEndTime = endValue;
      }
    }

    const rowRate = rowEndTime - rowStartTime;
    return calcHeightFromRate(rowRate, conversionRate);
  }, [conversionRate]);

  // 공통 타임라인 높이 계산
  const calcTimeLineHeight = useCallback((withTextOffset = false) => {
    const margin = index === 0 ? FIRST_INDEX_MARGIN : OTHER_INDEX_MARGIN;
    const offset = withTextOffset ? TEXT_OFFSET : 0;
    const baseHeight = lengthRate === 1 ? CELL_HEIGHT : (lengthRate * CELL_HEIGHT);

    let totalHeight = baseHeight + margin - offset;

    // rowCount만큼 높이 추가 (각 행의 실제 시간 범위 고려)
    if (rowCount > 1 && totalData && totalData.length > index) {
      for (let i = 1; i < rowCount; i++) {
        const rowIndex = index + i;

        if (rowIndex >= totalData.length) break;

        const rowHeight = calcRowHeight(totalData[rowIndex]);
        
        totalHeight += rowHeight + GAP;
      }
    }

    return totalHeight;
  }, [index, lengthRate, rowCount, totalData, calcRowHeight]);

  // 텍스트 포함한 구분선 길이 계산
  const textTimeLineHeight = useMemo(() => calcTimeLineHeight(true), [calcTimeLineHeight]);

  // 텍스트를 포함하지 않은 구분선 길이
  const timeLineHeight = useMemo(() => calcTimeLineHeight(false), [calcTimeLineHeight]);

  return (
    <TopLevelWrapper>
      {
          isNeedNewTime ?
          <TextWrapper>
            <TimeText>{viewTime}</TimeText>
            <TextTimeLine height={textTimeLineHeight}/>
          </TextWrapper>
          :
          <TimeLine height={timeLineHeight}/>
      }
    </TopLevelWrapper>
  )
}

export default React.memo(NewTimeDivisionLine);

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 42px;

  // margin-right: 14px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;
`;

const TimeText = styled.p`
  font-family: NotoSansKr-Medium;
  font-size: 14px;

  color: #777777;
  margin: 0 0 0 0;
`;

const TextTimeLine = styled.div`
  width: 2px;
  min-height: ${props => props.height}px;

  background-color: #ECECEC;
`;

const TimeLine = styled.div`
  width: 2px;
  height: ${props => props.height}px;

  background-color: #ECECEC;
`;
